/**
 * Two-Factor Authentication Service
 *
 * API client para operaciones de 2FA
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

export interface TwoFactorStatus {
  isConfigured: boolean
  isEnabled: boolean
  backupCodesRemaining: number
  verifiedAt: string | null
}

export interface Setup2FAResponse {
  qrCode: string // Data URL de imagen QR
  secret: string // Para entrada manual
  backupCodes: string[] // Mostrar solo una vez
  message: string
}

export interface Verify2FAResponse {
  verified: boolean
  message: string
  isFirstVerification: boolean
}

export interface Disable2FAResponse {
  disabled: boolean
  message: string
}

export interface Validate2FAResponse {
  valid: boolean
  message: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Obtiene el estado actual de 2FA del usuario
 */
export async function get2FAStatus(): Promise<TwoFactorStatus> {
  return get<TwoFactorStatus>(API_ROUTES.AUTH.TWO_FACTOR.STATUS)
}

/**
 * Inicia configuración de 2FA
 * Retorna QR y códigos de respaldo
 */
export async function setup2FA(): Promise<Setup2FAResponse> {
  return post<Setup2FAResponse>(API_ROUTES.AUTH.TWO_FACTOR.SETUP)
}

/**
 * Verifica código TOTP y activa 2FA
 */
export async function verify2FA(code: string): Promise<Verify2FAResponse> {
  return post<Verify2FAResponse>(API_ROUTES.AUTH.TWO_FACTOR.VERIFY, { code })
}

/**
 * Desactiva 2FA (requiere código válido)
 */
export async function disable2FA(code: string): Promise<Disable2FAResponse> {
  return post<Disable2FAResponse>(API_ROUTES.AUTH.TWO_FACTOR.DISABLE, { code })
}

/**
 * Valida código durante login
 */
export async function validate2FA(userId: string, code: string): Promise<Validate2FAResponse> {
  return post<Validate2FAResponse>(API_ROUTES.AUTH.TWO_FACTOR.VALIDATE, { userId, code })
}

/**
 * Respuesta de completar login con 2FA
 */
export interface Complete2FALoginResponse {
  user: {
    id: string
    email: string
    role: string
    isActive: boolean
    lastLoginAt: string | null
    createdAt: string
    updatedAt: string
  }
  accessToken: string
  refreshToken: string
  requires2FA: boolean
  requires2FASetup: boolean
  twoFactorEnabled: boolean
}

/**
 * Completa el login después de verificar código 2FA
 * Retorna tokens de acceso
 */
export async function complete2FALogin(userId: string, code: string): Promise<Complete2FALoginResponse> {
  return post<Complete2FALoginResponse>(API_ROUTES.AUTH.TWO_FACTOR.COMPLETE_LOGIN, { userId, code })
}
